import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import {
  AppConfigEntity,
  AppCustomerCompositeEntity,
  AppCustomerDetailsEntity,
  AppCustomerEntity,
  AppCustomerStubEntity,
  AppHeartbeatEntity,
  AppIdentityCompositeEntity,
  AppIdentityEntity,
  AppIdentitySlimCompositeEntity,
  AppInviteTokenData,
  AppInviteTokenEntity,
  AppMembershipEntity,
  AppMembershipInvitePayload,
  AppMembershipRoleEntity,
  AppMembershipRolePayloadEntity,
  AppNotificationDeliveryCompositeEntity,
  AppNotificationDeliveryEntity,
  AppUserEntity,
  AppUserRegistration,
  SystemEnvEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import {
  Observable,
  ReplaySubject,
  Subscription,
  forkJoin,
  fromEvent as observableFromEvent,
  of,
  throwError,
} from 'rxjs';
import { catchError, first, map, mergeMap, switchMap } from 'rxjs/operators';

import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { ApplicationApiClient, isNkHttpError } from '@nk/api-client-lib';
import { GENERIC_AUTH_SERVICE_TOKEN, GenericAuthService } from '@nk/auth-lib';
import { CachingService } from '@nk/caching-lib';
import { NkToastrService } from '@nk/ui-lib';
import { arrayMax, arrayToDict, getVisibilityChangeEventName, getVisibilityStateName } from '@nk/utils-lib';
import * as localforage from 'localforage';
import posthog from 'posthog-js';

const SLEEP_SECONDS = 120;

@Injectable()
export class AppService implements OnDestroy {
  private static _isHeartbeatRunning = false;
  private _heartBeatSubject = new ReplaySubject<AppHeartbeatEntity>(1);
  public heartBeat$ = this._heartBeatSubject.asObservable();
  private _configs: AppConfigEntity[];
  private _logoutToken = localStorage.getItem('logout_token');

  private _currentMouseSub: Subscription;
  private _visibilityChangeEventName = getVisibilityChangeEventName();
  private _visibilityStateName = getVisibilityStateName();

  // These current flags are true if they are valid at this moment in time.
  private _currentFocusFlag = true;
  private _currentForegroundFlag = this._visibilityChangeEventName == null ? null : true;

  // These period flags are true if they were ever true the between each heartbeat.
  private _periodActiveFlag = true; // User used the app since last heartbeat, i.e., moved the mouse over the application.
  private _periodFocusFlag = this._currentFocusFlag; // User had the application in focus since last heartbeat, i.e., switched to the application.
  private _periodForegroundFlag = this._currentForegroundFlag; // User had the application in foreground since last heartbeat, i.e., had the application open in a tab.
  private _previousHeartbeatSub: Subscription;

  constructor(
    private _applicationApi: ApplicationApiClient,
    private _toast: NkToastrService,
    private _cachingService: CachingService,
    private _router: Router,
    @Optional()
    @Inject(TranslateService)
    private _translateService?: TranslateService,
    @Inject(GENERIC_AUTH_SERVICE_TOKEN) private _authService?: GenericAuthService,
  ) {
    window.addEventListener('focus', () => {
      this._currentFocusFlag = true;
      this._periodFocusFlag = true;
    });
    window.addEventListener('blur', () => {
      this._currentFocusFlag = false;
    });
    window.addEventListener(this._visibilityChangeEventName, () => {
      this._currentForegroundFlag = !document[this._visibilityStateName];
      if (this._currentForegroundFlag) {
        this._periodForegroundFlag = true;
      }
    });
  }

  public fetchAppCustomerUuid(): Observable<string> {
    return this.identityDataCache.get().pipe(map((identity) => identity.app_customer.uuid));
  }

  public resendEmailVerification<T>(userEmail: string): Observable<T> {
    return this._applicationApi.post<T>('app-verify-email', { email: userEmail });
  }

  public systemEnvCache = this._cachingService.create(this, this.fetchSystemEnv);
  public fetchSystemEnv(): Observable<SystemEnvEntity> {
    return this._applicationApi.get<SystemEnvEntity>('system-env');
  }

  public configListCache = this._cachingService.create(this, this.fetchConfigList);
  public fetchConfigList(): Observable<AppConfigEntity[]> {
    return this._applicationApi.get<AppConfigEntity[]>('app-config', {}, null);
  }

  public configCache = this._cachingService.create(this, this.fetchConfig);
  public fetchConfig(configUuid: string): Observable<AppConfigEntity> {
    return this._applicationApi.get<AppConfigEntity>(`app-config/${configUuid}`, {}, null);
  }

  public notificationDeliveryCompositeCache = this._cachingService.create(
    this,
    this.fetchNotificationDeliveryCompositeCache,
  );
  public fetchNotificationDeliveryCompositeCache(
    membershipUuid: string,
  ): Observable<AppNotificationDeliveryCompositeEntity[]> {
    return this._applicationApi
      .get<
        AppNotificationDeliveryCompositeEntity[]
      >(`app-membership/${membershipUuid}/app-notification-delivery-composite`, {}, null)
      .pipe(
        map((data) =>
          data.sort(
            (a, b) =>
              b.app_notification_delivery.insert_timestamp.valueOf() -
              a.app_notification_delivery.insert_timestamp.valueOf(),
          ),
        ),
      );
  }

  public createAppMembership(partialAppMembership: Partial<AppMembershipEntity>): Observable<AppMembershipEntity> {
    return this._applicationApi.post<AppMembershipEntity>('app-membership', partialAppMembership, {}, null);
  }

  public deleteMembership(membershipUuid: string): Observable<AppMembershipEntity> {
    return this._applicationApi.delete<AppMembershipEntity>('app-membership/' + membershipUuid, {}, null);
  }

  public deleteAppMembershipInvitationByMembershipUuid(membershipUuid: string): Observable<AppMembershipEntity> {
    return this._applicationApi.delete<AppMembershipEntity>(
      'app-invite-membership/app-membership/' + membershipUuid,
      {},
      null,
    );
  }

  public appInviteMembership(invitePayload: AppMembershipInvitePayload): Observable<AppMembershipEntity[]> {
    return this._applicationApi.post<AppMembershipEntity[]>('app-invite-membership', invitePayload, {}, null);
  }

  public fetchAppInviteToken(appInviteTokenUuid: string): Observable<WithUuid<AppInviteTokenEntity>> {
    return this._applicationApi.get<WithUuid<AppInviteTokenEntity>>('app-invite-token/' + appInviteTokenUuid, {}, null);
  }

  public patchAppMembershipByAppInviteToken(
    appInviteTokenUuid: string,
    payload: Partial<AppMembershipEntity>,
  ): Observable<AppMembershipEntity> {
    return this._applicationApi.patch<AppMembershipEntity>(
      'app-invite-token/' + appInviteTokenUuid + '/app-membership',
      payload,
      {},
      null,
    );
  }

  public patchAppMembershipInviteStatusByAppInviteToken(
    appInviteTokenUuid: string,
    appInviteStatusKey: string,
  ): Observable<AppMembershipEntity> {
    const validInviteTokenStatusKey = [
      'initial', // membership is created, but user is not invited
      'complete', // membership is connected to user uuid
      'invited', // user has been invited
      'declined', // user declined
      'expired', // invitation expired.
    ];
    if (validInviteTokenStatusKey.indexOf(appInviteStatusKey) >= 0) {
      const payload = {
        app_invite_status_key: appInviteStatusKey,
      };
      return this._applicationApi.patch<AppMembershipEntity>(
        'app-invite-token/' + appInviteTokenUuid + '/app-membership',
        payload,
        {},
        null,
      );
    }
    return throwError(() => 'Invalid status key');
  }

  public fetchAppInviteTokenData(appInviteTokenUuid: string): Observable<AppInviteTokenData> {
    return forkJoin({
      appInviteToken: this.fetchAppInviteToken(appInviteTokenUuid),
    }).pipe(
      switchMap(({ appInviteToken }) =>
        forkJoin({
          appUsers: this.fetchAppUserByAppInviteToken(appInviteTokenUuid),
          appMemberships: this.fetchAppMembershipByAppInviteToken(appInviteTokenUuid),
          appCustomers: this.fetchAppCustomerStubByAppInviteToken(appInviteTokenUuid),
        }).pipe(
          map(({ appUsers, appMemberships, appCustomers }) => ({
            user: appUsers?.[0] ?? null,
            customer: (appCustomers?.[0] as AppCustomerEntity) ?? null,
            membership: appMemberships?.[0] ?? null,
            appInviteToken,
          })),
        ),
      ),
    );
  }

  public fetchAppCustomerStubByAppInviteToken(appInviteTokenUuid: string): Observable<AppCustomerStubEntity[]> {
    return this._applicationApi.get<AppCustomerStubEntity[]>(
      `app-invite-token/${appInviteTokenUuid}/app-customer-stub`,
      {},
      null,
    );
  }

  // only owners of app-invite-token should be able to do this.
  public fetchAppUserByAppInviteToken(appInviteTokenUuid: string): Observable<AppUserEntity[]> {
    return this._applicationApi.get<AppUserEntity[]>(`app-invite-token/${appInviteTokenUuid}/app-user`, {}, null);
  }

  public fetchAppMembershipByAppInviteToken(uuid: string): Observable<AppMembershipEntity[]> {
    return this._applicationApi.get<AppMembershipEntity[]>(`app-invite-token/${uuid}/app-membership`, {}, null);
  }

  public fetchCustomerByAppMembership(uuid: string): Observable<AppCustomerEntity[]> {
    return this._applicationApi.get<AppCustomerEntity[]>(`app-membership/${uuid}/app-customer`, {}, null);
  }

  // TODO: Create and use `AppUserRegistrationEntity`. It has keys app_invite_token_uuid, password and full_name
  public createAppUserRegistration(payload: AppUserRegistration): Observable<AppUserRegistration> {
    return this._applicationApi.post('app-user-registration', payload, {}, null);
  }

  public patchNotificationDeliveries(
    uuid: string,
    notificationPatch: Partial<AppNotificationDeliveryEntity>,
  ): Observable<AppNotificationDeliveryEntity> {
    return this._applicationApi.patch<AppNotificationDeliveryEntity>(
      'app-notification-delivery/' + uuid,
      notificationPatch,
      {},
      null,
    );
  }

  public identityDataByCustomerCache = this._cachingService.create(this, this.fetchIdentityDataByCustomer);
  public fetchIdentityDataByCustomer(customerUuid: string): Observable<AppIdentitySlimCompositeEntity[]> {
    return forkJoin([
      this.appCustomerCache.get(customerUuid).pipe(first()),
      this.appCustomerDetailsByAppCustomerCache.get(customerUuid).pipe(first()),
      this.appUserListByCustomerCache.get(customerUuid).pipe(first()),
      this.appMembershipListByCustomerCache.get(customerUuid).pipe(first()),
    ]).pipe(
      map((payload) => {
        const [customer, customerDetails, users, memberships] = payload;
        const membershipByUserDict = arrayToDict(memberships, (u) => u.app_user_uuid);
        return users.map((user) => {
          const membership = membershipByUserDict[user.uuid];
          const customerCopy = Object.assign({}, customer);
          return {
            app_user: user,
            app_customer: customerCopy,
            app_customer_details: customerDetails?.length > 0 ? customerDetails[0] : null,
            app_membership: membership,
          };
        });
      }),
    );
  }

  public identityDataCache = this._cachingService.create(this, this.fetchIdentityData);
  public fetchIdentityData(): Observable<AppIdentityCompositeEntity> {
    return this._authService.customerSlug$.pipe(
      switchMap((customerSlug) => {
        console.info('customer slug', customerSlug);
        if (!customerSlug) {
          console.error('Could not find customerslug');
        }
        customerSlug = localStorage.getItem('customer_slug');
        if (!customerSlug) {
          console.error('Coult not find customer slug in localstorage either');
        }
        return this.fetchIdentityCompositeList(customerSlug);
      }),
      map((identity) => {
        if (identity.length === 1) {
          return identity[0];
        } else {
          this._authService.removeCustomerSlug();
          this._authService.logout('wrongCustomer');
        }
      }),
      catchError((error: unknown) => {
        if (isNkHttpError(error)) {
          posthog.capture('$error', {
            endpoint: 'fetchIdentityData',
            status: error.status,
            message: error.error.message,
          });
          if (error.status === 403) {
            console.error(error);
            this._authService.removeCustomerSlug();
            this._authService.logout('wrongCustomer');
          } else if (error.status === 404) {
            console.error(error);
            this._authService.removeCustomerSlug();
            this._authService.logout('missingCustomer');
          }
        } else {
          posthog.capture('$error', {
            endpoint: 'fetchIdentityData',
            status: 'unknown',
            message: error,
          });
        }
        return throwError(() => error);
      }),
    );
  }

  public putAppEnterpriseIdentity(): Observable<{ message: string } | null> {
    return this._applicationApi.put<{ message: string } | null>('app-enterprise-identity', {}, null);
  }

  public appIdentityListCache = this._cachingService.create(this, this.fetchAppIdentityList);
  public fetchAppIdentityList(): Observable<AppIdentityEntity[]> {
    return this._applicationApi.get<AppIdentityEntity[]>('app-identity', {}, null);
  }

  public appIdentityCompositeListCache = this._cachingService.create(this, this.fetchIdentityCompositeList);
  public fetchIdentityCompositeList(appCustomerSlug: string = undefined): Observable<AppIdentityCompositeEntity[]> {
    let httpParams = new HttpParams();

    if (appCustomerSlug) {
      httpParams = httpParams.append('app_customer_slug', appCustomerSlug);
    }
    return this._applicationApi.get<AppIdentityCompositeEntity[]>(
      'app-identity-composite',
      { params: httpParams },
      null,
    );
  }

  public appUserCache = this._cachingService.create(this, this.fetchAppUser);
  public fetchAppUser(userUuid: string): Observable<AppUserEntity> {
    return this._applicationApi.get<AppUserEntity>(`app-user/${userUuid}`, {}, null);
  }

  public updateUserLanguageCode(userUuid: string, languageCode: string): Observable<AppUserEntity> {
    return this._applicationApi.patch<AppUserEntity>(`app-user/${userUuid}`, { language_code: languageCode }, {}, null);
  }

  public appCustomerListCache = this._cachingService.create(this, this.fetchAppCustomerList);
  public fetchAppCustomerList(): Observable<WithUuid<AppCustomerEntity>[]> {
    return this._applicationApi.get<WithUuid<AppCustomerEntity>[]>('app-customer', {}, null);
  }
  public appCustomerCache = this._cachingService.create(this, this.fetchAppCustomer);
  public fetchAppCustomer(customerUuid: string): Observable<WithUuid<AppCustomerEntity>> {
    return this._applicationApi.get<WithUuid<AppCustomerEntity>>(`app-customer/${customerUuid}`, {}, null);
  }

  public appCustomerCompositeCache = this._cachingService.create(this, this.fetchAppCustomerComposite);
  public fetchAppCustomerComposite(appCustomerUuid: string): Observable<AppCustomerCompositeEntity> {
    return this._applicationApi.get<AppCustomerCompositeEntity>(`app-customer-composite/${appCustomerUuid}`);
  }

  public appMembershipCache = this._cachingService.create(this, this.fetchAppMembership);
  public fetchAppMembership(membershipUuid: string): Observable<WithUuid<AppMembershipEntity>> {
    return this._applicationApi.get<WithUuid<AppMembershipEntity>>(`app-membership/${membershipUuid}`, {}, null);
  }

  public appMembershipListByUserCache = this._cachingService.create(this, this.fetchAppMembershipListByUser);
  public fetchAppMembershipListByUser(userUuid: string): Observable<WithUuid<AppMembershipEntity>[]> {
    return this._applicationApi.get<WithUuid<AppMembershipEntity>[]>(`app-user/${userUuid}/app-membership`, {}, null);
  }

  public appUserListByCustomerCache = this._cachingService.create(this, this.fetchAppUserListByCustomer);
  public fetchAppUserListByCustomer(customerUuid: string): Observable<WithUuid<AppUserEntity>[]> {
    return this._applicationApi.get<WithUuid<AppUserEntity>[]>(`app-customer/${customerUuid}/app-user`, {}, null);
  }

  public appMembershipListByCustomerCache = this._cachingService.create(this, this.fetchAppMembershipListByCustomer);
  public fetchAppMembershipListByCustomer(customerUuid: string): Observable<WithUuid<AppMembershipEntity>[]> {
    return this._applicationApi.get<WithUuid<AppMembershipEntity>[]>(
      `app-customer/${customerUuid}/app-membership`,
      {},
      null,
    );
  }

  public getAppMembershipListByCustomerCache(): Observable<WithUuid<AppMembershipEntity>[]> {
    return this.identityDataCache.get().pipe(
      map((identity) => identity.app_customer.uuid),
      switchMap((appCustomerUuid) => this.appMembershipListByCustomerCache.get(appCustomerUuid)),
    );
  }

  public appCustomerDetailsByAppCustomerCache = this._cachingService.create(
    this,
    this.fetchAppCustomerDetailsByAppCustomer,
  );
  public fetchAppCustomerDetailsByAppCustomer(
    appCustomerUuid: string,
  ): Observable<WithUuid<AppCustomerDetailsEntity>[]> {
    return this._applicationApi.get<WithUuid<AppCustomerDetailsEntity>[]>(
      `app-customer/${appCustomerUuid}/app-customer-details`,
      {},
      null,
    );
  }

  public fetchAppCustomerDetails(): Observable<AppCustomerDetailsEntity[]> {
    return this.identityDataCache.get().pipe(
      switchMap((appIdentityCompositeEntity) => {
        return this.appCustomerDetailsByAppCustomerCache.get(appIdentityCompositeEntity.app_customer.uuid);
      }),
    );
  }

  public startHeartbeat(): void {
    if (!AppService._isHeartbeatRunning) {
      AppService._isHeartbeatRunning = true;
      this._runHeartbeat(true);
      setInterval(() => {
        this._runHeartbeat(false);
      }, SLEEP_SECONDS * 1000);
    }
  }

  private _runHeartbeat(firstRun: boolean): void {
    if (this._currentMouseSub) {
      this._currentMouseSub.unsubscribe();
      this._currentMouseSub = null;
    }
    let configObservable: Observable<AppConfigEntity[]>;

    if (this._configs) {
      configObservable = of(this._configs);
    } else {
      configObservable = this.configListCache.fetch();
    }

    this._currentMouseSub = observableFromEvent(document.body, 'mousemove')
      .pipe(first())
      .subscribe(() => {
        this._periodActiveFlag = true;
      });

    if (this._previousHeartbeatSub && !this._previousHeartbeatSub.closed) {
      this._previousHeartbeatSub.unsubscribe();
    }

    this._previousHeartbeatSub = configObservable
      .pipe(
        mergeMap((configs) => {
          if (!this._configs) {
            this._configs = configs;
          }
          if (!this._logoutToken) {
            this._logoutToken = configs.find((c) => c.config_key === 'logout_token').config_value;
            localStorage.setItem('logout_token', this._logoutToken);
          }
          return this._fetchHeartbeat(
            this._logoutToken,
            configs.find((c) => c.config_key === 'application_version').config_value,
            this._periodActiveFlag,
            this._periodFocusFlag,
            this._periodForegroundFlag,
          );
        }),
        first(),
      )
      .subscribe({
        next: (heartBeat) => {
          this._authService.renewTokenIfStale();

          this._periodActiveFlag = false;
          this._periodFocusFlag = this._currentFocusFlag;
          this._periodForegroundFlag = this._currentForegroundFlag;

          if (heartBeat.logout_client_flag) {
            this._authService.logout('forceLogout');
          }
          if (heartBeat.refresh_client_flag) {
            console.error('Refresh client flag');
            void localforage.clear();
            location.reload();
          }
          this._heartBeatSubject.next(heartBeat);
          if (!firstRun) {
            // this._authService.renewTokens();
          }
        },
        error: (err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status === 0 || err.status === 500) {
              if (this._translateService) {
                this._toast.error(
                  this._translateService.instant(extractTranslation('common.errors.connection_lost.text')),
                  this._translateService.instant(extractTranslation('common.errors.connection_lost.title')),
                );
              } else {
                this._toast.error(
                  'Lost connection to server or internet. Try refreshing the page if the problem persists.',
                );
              }
            }
            if (err.status === 401 || err.status === 403) {
              if (this._translateService) {
                this._toast.error(
                  this._translateService.instant(extractTranslation('common.errors.forbidden.text')),
                  this._translateService.instant(extractTranslation('common.errors.forbidden.title')),
                );
              } else {
                this._toast.error('You have no access to the resource you are trying to reach');
              }
            }
          } else {
            if (this._translateService) {
              this._toast.error(
                this._translateService.instant(extractTranslation('common.errors.connection_lost.text')),
                this._translateService.instant(extractTranslation('common.errors.connection_lost.title')),
              );
            } else {
              this._toast.error(
                'Lost connection to server or internet. Try refreshing the page if the problem persists.',
              );
            }
          }
        },
      });
  }

  private _fetchHeartbeat(
    logoutToken: string,
    applicationVersion: string,
    activeFlag: boolean,
    focusFlag: boolean,
    foregroundFlag?: boolean,
  ): Observable<AppHeartbeatEntity> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('logout_token', logoutToken);
    httpParams = httpParams.append('application_version', applicationVersion);
    httpParams = httpParams.append('active_flag', String(activeFlag));
    httpParams = httpParams.append('focus_flag', String(focusFlag));
    if (!(foregroundFlag == null)) {
      // MUST BE `!(x==y)` to check both null and undefined
      httpParams = httpParams.append('foreground_flag', String(foregroundFlag));
    }

    const identity = this.identityDataCache.snapshot();

    if (identity) {
      const notificationDeliveryComposites = this.notificationDeliveryCompositeCache.snapshot(
        identity.app_membership.uuid,
      );
      if (notificationDeliveryComposites) {
        const notificationDeliveries = notificationDeliveryComposites.map((i) => i.app_notification_delivery);
        const latestNotificationDeliveries = arrayMax(notificationDeliveries, (nd) => nd.insert_timestamp);
        if (latestNotificationDeliveries) {
          httpParams = httpParams.append('notification_uuid', latestNotificationDeliveries.uuid);
          httpParams = httpParams.append('app_membership_uuid', identity.app_membership.uuid);
        }
      }
    } else {
      console.warn('No identity');
    }
    return this._applicationApi.get<AppHeartbeatEntity>('app-heartbeat', { params: httpParams }, null);
  }

  public adminDashboardUrlListCache = this._cachingService.create(this, this.fetchAdminDashboardUrlList);
  public fetchAdminDashboardUrlList(): Observable<string[]> {
    return this._applicationApi.get<string[]>('app-admin-dashboard-url');
  }
  public postMembershipRole(
    customerUuid: string,
    payload: AppMembershipRolePayloadEntity,
  ): Observable<AppMembershipRoleEntity> {
    // TODO Self-service: Payload is missing typing.
    return this._applicationApi.post<AppMembershipRoleEntity>(
      `app-customer/${customerUuid}/app-membership-role`,
      payload,
      {},
      null,
    );
  }

  public deleteMembershipRole(
    appCustomerUuid: string,
    appMembershipRoleUuid: string,
  ): Observable<AppMembershipRoleEntity> {
    return this._applicationApi.delete<AppMembershipRoleEntity>(
      `app-customer/${appCustomerUuid}/app-membership-role/${appMembershipRoleUuid}`,
      {},
      null,
    );
  }

  public authValidCustomerIsChosen(): Observable<boolean> {
    return this._authService.authInfo$.pipe(
      mergeMap((authInfo) => {
        if (authInfo.isValid()) {
          if (this._authService.customerSlug) {
            return of(true);
          } else {
            // Put in error if no connection to server.
            return this.appIdentityListCache.fetch().pipe(
              map((identityList) => {
                if (identityList && identityList.length === 1) {
                  const identity = identityList[0];
                  this._authService.setCustomerSlug(identity.app_customer_slug);
                  return true;
                } else {
                  this._router.navigate(['/login']);
                  return false;
                }
              }),
            );
          }
        } else {
          void this._router.navigate(['/login']);
          return of(false);
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    if (this._currentMouseSub) {
      this._currentMouseSub.unsubscribe();
    }
  }
}
